import { z } from "zod";
export const EstadoDianSchema = z.enum([
    "BORRADOR",
    "PENDIENTE",
    "ACEPTADO",
    "RECHAZADO",
    "REALIZADO",
]);
export const GuardarConfiguracionDianSchema = z.object({
    resolucionNumero: z.string().min(2, "Número de resolución obligatorio").max(60),
    resolucionFecha: z.string().min(1, "Fecha de resolución obligatoria"),
    prefijo: z.string().max(10, "Prefijo máximo 10 caracteres").default("POS"),
    rangoDesde: z.number().int().min(1, "Rango inicial debe ser >= 1"),
    rangoHasta: z.number().int().min(1, "Rango final debe ser >= 1"),
    consecutivoActual: z.number().int().min(0, "Consecutivo actual debe ser >= 0"),
    claveTecnica: z.string().max(240).optional().nullable(),
    proveedorTecnologico: z.string().max(60).default("SIIGO_SIMULADO"),
    modoOperacion: z.enum(["PRUEBAS", "PRODUCCION"]).default("PRUEBAS"),
    factusClientId: z.string().max(120).optional().nullable(),
    factusClientSecret: z.string().max(240).optional().nullable(),
    factusUsername: z.string().max(120).optional().nullable(),
    factusPassword: z.string().max(240).optional().nullable(),
    factusNumberingRangeId: z.number().int().positive().optional().nullable(),
    impuestoTipo: z.enum(["IVA", "INC", "EXCLUIDO"]).default("INC"),
    impuestoTarifa: z.number().min(0).max(100).default(8),
});
export const EmitirFacturaDianSchema = z.object({
    facturaId: z.string().uuid("ID de factura inválido"),
    clienteEmail: z.string().email("Correo electrónico inválido").optional().nullable(),
    clienteDocumento: z.string().max(30).optional().nullable(),
    clienteNombre: z.string().max(120).optional().nullable(),
});
export const FiltroSolicitudesDianQuerySchema = z.object({
    estado: EstadoDianSchema.optional(),
    limite: z.coerce.number().int().min(1).max(100).default(50),
});
// Resultado que devuelve la API DIAN de DataSoftCode (instalada en el PC del
// local) después de facturar una venta pendiente. Ver DianService.registrarResultadoConector.
export const ResultadoConectorDianSchema = z
    .object({
    estado: z.enum(["APROBADO", "SIMULADO", "ENVIADO", "RECHAZADO", "ERROR"]),
    numero: z.string().optional().nullable(),
    prefijo: z.string().optional().nullable(),
    consecutivo: z.number().int().optional().nullable(),
    tipo_documento: z.string().optional().nullable(),
    codigo_unico: z.string().optional().nullable(),
    qr_url: z.string().optional().nullable(),
    mensaje: z.string().optional().nullable(),
    errores: z.array(z.string()).optional().nullable(),
})
    .passthrough();
export const FiltroPendientesConectorQuerySchema = z.object({
    soloContar: z.coerce.boolean().optional(),
});
// ------------------------------------------------------------------
// Configuración de la API DIAN del PC de la caja, administrada desde aquí
// ------------------------------------------------------------------
const textoOpcional = (max) => z.string().trim().max(max).optional().default("");
const fecha = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Fecha inválida (AAAA-MM-DD)");
export const NumeracionDatasoftcodeSchema = z.object({
    software_id: textoOpcional(60),
    pin: textoOpcional(10),
    test_set_id: textoOpcional(60),
    resolucion: z.string().trim().min(1, "Número de resolución obligatorio").max(30),
    prefijo: z.string().trim().max(10).default(""),
    desde: z.number().int().min(0),
    hasta: z.number().int().min(1),
    fecha_desde: fecha,
    fecha_hasta: fecha,
    clave_tecnica: textoOpcional(120),
}).refine((n) => n.desde <= n.hasta, { message: "El rango 'desde' debe ser menor o igual que 'hasta'", path: ["hasta"] });
export const ConfigDatasoftcodeSchema = z.object({
    ambiente: z.enum(["simulado", "2", "1"]),
    emisor: z.object({
        tipo_persona: z.enum(["1", "2"]),
        nit: z.string().trim().regex(/^\d{5,15}$/, "NIT sin puntos ni dígito de verificación"),
        dv: z.string().trim().regex(/^\d$/, "Dígito de verificación inválido"),
        razon_social: z.string().trim().min(2).max(200),
        nombre_comercial: textoOpcional(200),
        correo: textoOpcional(120),
        telefono: textoOpcional(30),
        responsabilidades_fiscales: z.string().trim().max(60).default("R-99-PN"),
        responsable_iva: z.boolean().default(false),
        direccion: z.object({
            linea: z.string().trim().min(3).max(200),
            municipio: z.string().trim().min(2).max(80),
            municipio_codigo: z.string().trim().regex(/^\d{5}$/, "Código DIVIPOLA de 5 dígitos"),
            departamento: z.string().trim().min(2).max(80),
            departamento_codigo: z.string().trim().regex(/^\d{2}$/, "Código de departamento de 2 dígitos"),
            codigo_postal: textoOpcional(10),
        }),
    }),
    documentos: z.object({
        pos: NumeracionDatasoftcodeSchema,
        factura: NumeracionDatasoftcodeSchema,
    }),
    // Solo se envía cuando el admin quiere mover el siguiente número a mano.
    consecutivos: z.object({ pos: z.number().int().min(0).optional(), factura: z.number().int().min(0).optional() }).optional(),
});
export const SubirCertificadoDatasoftcodeSchema = z.object({
    nombre: z.string().max(200),
    contenido: z.string().min(100, "Archivo vacío").max(200_000, "El archivo es demasiado grande para ser un certificado"),
    clave: z.string().max(200),
});
/** Lo que reporta el PC en cada ciclo del conector. */
export const EstadoPcDatasoftcodeSchema = z
    .object({
    version_aplicada: z.number().int().optional().nullable(),
    error_configuracion: z.string().optional().nullable(),
    llave_publica: z.string().max(2000).optional().nullable(),
    ambiente: z.string().optional().nullable(),
    certificado: z
        .object({ sujeto: z.string().optional(), emisor: z.string().optional(), valido_hasta: z.string().optional(), nombre: z.string().optional().nullable() })
        .passthrough()
        .optional()
        .nullable(),
    consecutivos: z.object({ pos: z.number().optional(), factura: z.number().optional() }).optional().nullable(),
    documentos: z.record(z.number()).optional().nullable(),
    version_app: z.string().optional().nullable(),
    equipo: z.string().optional().nullable(),
})
    .passthrough();
export const FiltroConfigConectorQuerySchema = z.object({
    version: z.coerce.number().int().min(0).default(0),
});
// ------------------------------------------------------------------
// Respuestas (lo que el backend REALMENTE devuelve).
// ------------------------------------------------------------------
const MontoCoercible = z.coerce.number();
export const ConfiguracionDianRespuestaSchema = z.object({
    id: z.string().optional(),
    sucursalId: z.string(),
    resolucionNumero: z.string(),
    resolucionFecha: z.string(),
    prefijo: z.string(),
    rangoDesde: z.number(),
    rangoHasta: z.number(),
    consecutivoActual: z.number(),
    claveTecnica: z.string().nullable().optional(),
    proveedorTecnologico: z.string(),
    modoOperacion: z.enum(["PRUEBAS", "PRODUCCION"]),
    factusClientId: z.string().nullable().optional(),
    factusUsername: z.string().nullable().optional(),
    factusNumberingRangeId: z.number().nullable().optional(),
    tieneCredencialesFactus: z.boolean().optional(),
    impuestoTipo: z.enum(["IVA", "INC", "EXCLUIDO"]),
    impuestoTarifa: MontoCoercible,
    configurado: z.boolean(),
});
export const FacturaConDianSchema = z.object({
    id: z.string(),
    sucursalId: z.string(),
    mesaId: z.string().nullable().optional(),
    mesaSesionId: z.string().nullable().optional(),
    cajeroId: z.string(),
    clienteNombre: z.string().nullable().optional(),
    clienteDocumento: z.string().nullable().optional(),
    clienteEmail: z.string().nullable().optional(),
    subtotal: MontoCoercible,
    descuento: MontoCoercible,
    propina: MontoCoercible,
    total: MontoCoercible,
    tipoDocumento: z.string().nullable().optional(),
    prefijoDian: z.string().nullable().optional(),
    consecutivoDian: z.number().nullable().optional(),
    cufe: z.string().nullable().optional(),
    qrDianUrl: z.string().nullable().optional(),
    estadoDian: z.string().nullable().optional(),
    dianEnviadoEn: z.string().nullable().optional(),
    creadoEn: z.string(),
});
export const PayloadDianSchema = z.object({
    encabezado: z.object({
        tipoDocumento: z.string(),
        prefijo: z.string(),
        consecutivo: z.number(),
        numeroCompleto: z.string(),
        resolucionNumero: z.string(),
        resolucionFecha: z.string(),
        fechaEmision: z.string(),
        cufe: z.string(),
        qrDianUrl: z.string(),
    }),
    emisor: z.object({
        nit: z.string(),
        razonSocial: z.string(),
        sucursalId: z.string(),
        modoOperacion: z.string(),
    }),
    adquiriente: z.object({
        nombre: z.string(),
        documento: z.string(),
        email: z.string(),
    }),
    totales: z.object({
        subtotal: MontoCoercible,
        descuento: MontoCoercible,
        propina: MontoCoercible,
        total: MontoCoercible,
    }),
    proveedorTecnologico: z.object({
        nombre: z.string(),
        estado: z.string(),
        codigoRespuesta: z.string(),
        trackId: z.string(),
    }),
});
export const EmitirFacturaDianRespuestaSchema = z.object({
    factura: FacturaConDianSchema,
    payloadDian: PayloadDianSchema,
});
//# sourceMappingURL=dian.schema.js.map